import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import client from '../api/client'

const DISMISSED_KEY = 'ciBannerDismissedVersion'
// 2 minutes: frequent enough that a scheduled window boundary or an admin
// edit surfaces on an already-open tab within a couple of minutes,
// infrequent enough to add negligible load (see design.md's Decisions).
const POLL_INTERVAL_MS = 2 * 60 * 1000

function SystemBanner() {
  const { t } = useTranslation()
  const [banner, setBanner] = useState(null) // { message, severity, updated_at } | null
  const [dismissedVersion, setDismissedVersion] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY)
    } catch {
      return null
    }
  })

  const fetchBanner = useCallback(() => {
    client.get('/banner')
      .then(resp => setBanner(resp.data.active ? resp.data : null))
      .catch(err => {
        // Report rather than swallow. A failed poll looks exactly like "no
        // banner is configured" on screen, so staying silent here sends people
        // hunting for a bug in the banner when the real cause is elsewhere —
        // an expired session returning 401, or the API returning 500. The
        // banner still renders nothing, which is the right outcome; what
        // changes is that the reason is visible.
        const status = err?.response?.status
        console.warn(
          `[SystemBanner] could not fetch /banner${status ? ` (HTTP ${status})` : ''}; retrying in ${POLL_INTERVAL_MS / 1000}s`,
          err,
        )
      })
  }, [])

  useEffect(() => {
    fetchBanner()
    const id = setInterval(fetchBanner, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [fetchBanner])

  if (!banner || banner.updated_at === dismissedVersion) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, banner.updated_at)
    } catch { /* localStorage unavailable — dismissal just won't persist */ }
    setDismissedVersion(banner.updated_at)
  }

  return (
    <div className={`system-banner system-banner--${banner.severity}`} role="alert">
      <span className="system-banner-message">{banner.message}</span>
      <button
        type="button"
        className="system-banner-dismiss"
        onClick={dismiss}
        aria-label={t('common.dismiss')}
      >
        &times;
      </button>
    </div>
  )
}

export default SystemBanner
